import React, { startTransition } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSession } from "next-auth/react";
import { FEDERAL_WORKER_USERTYPE } from "@/lib/constants";
import { getMarketplaceUrl } from "@/lib/marketplace-utils";

export default function MobileNav({
  isOpen,
  setIsOpen,
  menu,
  pathname,
  isHomePage,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  menu: { label: string; href: string; disabled: boolean; icon?: React.ElementType }[];
  pathname: string;
  isHomePage: boolean;
}) {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  const session = useSession();

  React.useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Bars3Icon
          className={cn("text-icon-link size-6 stroke-2 md:hidden", !isHomePage && "text-icon-title-inv")}
          title='Menu'
        />
      </SheetTrigger>
      <SheetContent
        side='left'
        className={cn(
          "w-full data-[state=closed]:duration-200 data-[state=open]:duration-300",
          !isHomePage && "w-4/5 border-none"
        )}
      >
        <div
          className={cn(
            "absolute top-2 right-4 left-2 z-50 flex h-10 items-center justify-between rounded-full bg-transparent px-4 md:right-10 md:left-10 md:px-10 2xl:h-14 2xl:px-12",
            isHomePage && "md:bg-bg-tetiary",
            !isHomePage && "flex-row-reverse items-center justify-between"
          )}
        >
          <SheetClose asChild>
            <XMarkIcon className='text-icon-title size-4 stroke-3' title='Close' />
          </SheetClose>
          <Link
            href='/'
            onClick={() => setIsOpen(false)}
            title='Onyx Home'
            className={cn(
              "absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:translate-x-0",
              !isHomePage && "relative left-0 translate-x-0"
            )}
          >
            <Image
              src={mounted && theme === "dark" ? "/assets/icons/logo-dark.svg" : "/assets/icons/logo-light.svg"}
              alt='Onyx Logo'
              width={24}
              height={24}
              className='size-5 md:size-6 2xl:size-8'
            />
          </Link>
          <div
            className={cn(
              "flex items-center gap-2 px-0 md:gap-4",
              isHomePage || (session.data?.user.token && "hidden")
            )}
          >
            <Button
              asChild
              variant='ghost'
              onClick={() => setIsOpen(false)}
              className='hover:text-link-primary text-link-primary md:text-link-muted px-0 text-xs font-semibold hover:bg-transparent md:text-sm 2xl:text-base dark:hover:bg-transparent'
            >
              <Link href={getMarketplaceUrl(`/register/${FEDERAL_WORKER_USERTYPE}/sign-up`)}>Sign Up</Link>
            </Button>
            <Button
              asChild
              variant='ghost'
              onClick={() => setIsOpen(false)}
              className='hover:text-link-primary text-link-primary md:text-link-muted px-0 text-xs font-semibold hover:bg-transparent md:text-sm 2xl:text-base dark:hover:bg-transparent'
            >
              <Link href={getMarketplaceUrl("/login")}>Login</Link>
            </Button>
          </div>
        </div>
        <div
          className={cn(
            "mt-20 flex flex-col items-center justify-center gap-4",
            !isHomePage && "w-full items-start justify-start gap-0 px-4"
          )}
        >
          {menu.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              onClick={(e) => {
                setIsOpen(false);
                if (item.href.startsWith("#")) {
                  e.preventDefault();
                  const targetId = item.href.substring(1);
                  const targetElement = document.getElementById(targetId);
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              }}
              className={cn(
                "hover:text-link-primary text-link-muted text-xs font-semibold duration-300 ease-in-out md:text-sm 2xl:text-base",
                pathname === item.href && "",
                !isHomePage &&
                  "hover:text-text-title-inv hover:bg-bg-base-inv focus:text-text-title-inv focus:bg-bg-base-inv text-text-title relative flex h-14 w-full items-center gap-3 rounded-xl px-4 text-base font-normal transition-all duration-200 ease-in-out",
                pathname.includes(item.href) &&
                  !isHomePage &&
                  "text-text-title-inv bg-bg-base-inv my-0.5 transition-all duration-200 ease-in-out first:mt-0 last:mb-0"
              )}
            >
              {item.icon && <item.icon className='size-5 stroke-2' />}
              {item.label}
              {!isHomePage && (
                <span className='bg-border-primmary/20 absolute right-5 bottom-0 left-5 h-[0.5px]'></span>
              )}
            </Link>
          ))}
        </div>
        <Image
          src='/assets/images/hero-img.png'
          alt='Hero Image'
          width={430}
          height={430}
          className={cn("w-full rounded-full", !isHomePage && "hidden md:block")}
        />
      </SheetContent>
    </Sheet>
  );
}
